import React, { useState, useEffect, useRef, useCallback } from 'react';

const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=`;
const API_KEY = process.env.REACT_APP_API_KEY;
const PDF_JS_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.min.js";
const PDF_JS_WORKER_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js";
function PdfPageRenderer({ pdfDoc, pageNum }) {
  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  useEffect(() => {
    if (!pdfDoc) return;

    let isCancelled = false;

    const render = async () => {
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
          console.warn("Error cancelling previous render task:", e);
        }
        renderTaskRef.current = null;
      }

      try {
        const page = await pdfDoc.getPage(pageNum);
        if (isCancelled) return;

        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvasRef.current;

        if (!canvas) return;

        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        const renderContext = {
          canvasContext: context,
          viewport: viewport,
        };

        const task = page.render(renderContext);
        renderTaskRef.current = task;

        await task.promise;

        renderTaskRef.current = null;
      } catch (e) {
        renderTaskRef.current = null;
        if (e.name === 'RenderingCancelledException') {
          console.log(`Render cancelled for page ${pageNum}`);
        } else {
          console.error(`Error rendering page ${pageNum}:`, e);
        }
      }
    };

    render();

    return () => {
      isCancelled = true;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch (e) {
        }
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, pageNum]);

  return (
    <div id={`pdf-page-${pageNum}`} className="mb-4">
      <canvas ref={canvasRef} />
    </div>
  );
}


export default function App() {
  const [pdfFile, setPdfFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fullDocumentText, setFullDocumentText] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoadingAiResponse, setIsLoadingAiResponse] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [totalPages, setTotalPages] = useState(0);
  const [error, setError] = useState(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  const chatContainerRef = useRef(null);

  useEffect(() => {
    if (window.pdfjsLib) {
      setIsScriptLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = PDF_JS_SRC;
    script.onload = () => {
      setIsScriptLoaded(true);
    };
    script.onerror = () => {
      setError("Failed to load PDF library. Please refresh the page.");
    }
    document.body.appendChild(script);

    return () => {
      const scripts = document.querySelectorAll(`script[src="${PDF_JS_SRC}"]`);
      scripts.forEach(s => s.remove());
    }
  }, []);


  const renderPage = useCallback(async (pageNum) => {
    const element = document.getElementById(`pdf-page-${pageNum}`);
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);


  const loadPdf = useCallback(async (file) => {
    if (!file) return;

    setIsProcessing(true);
    setError(null);
    setChatHistory([]);
    setFullDocumentText("");
    setPdfDoc(null);
    setTotalPages(0);

    try {
      if (!window.pdfjsLib) {
        setError("PDF library is not loaded yet. Please wait a moment and try again.");
        setIsProcessing(false);
        setPdfFile(null);
        return;
      }

      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER_SRC;

      const fileReader = new FileReader();

      fileReader.onload = async () => {
        try {
          const typedarray = new Uint8Array(fileReader.result);
          const doc = await window.pdfjsLib.getDocument({ data: typedarray }).promise;

          if (doc.numPages > 3) {
            setError("Only PDFs with 3 pages or less are allowed.");
            setIsProcessing(false);
            setPdfFile(null);
            return;
          }

          setPdfDoc(doc); // Set the doc state
          setTotalPages(doc.numPages); // Set total pages

          // Extract text from all pages
          let allText = "";
          for (let i = 1; i <= doc.numPages; i++) {
            const page = await doc.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map(item => item.str).join(' ');
            allText += `[Page ${i}]\n${pageText}\n\n`;
          }

          setFullDocumentText(allText);
          setIsProcessing(false);
          setChatHistory([
            {
              from: 'ai',
              text: `Your document is ready! It has ${doc.numPages} pages. You can now ask questions about its content.`
            }
          ]);
        } catch (e) {
          console.error("Error loading/parsing PDF:", e);
          setError("Failed to load or parse the PDF. Please try a different file.");
          setIsProcessing(false);
          setPdfFile(null);
        }
      };

      fileReader.onerror = () => {
        setError("Failed to read the file.");
        setIsProcessing(false);
        setPdfFile(null);
      };

      fileReader.readAsArrayBuffer(file);

    } catch (e) {
      console.error("Error setting up PDF.js:", e);
      setError("An error occurred while setting up the PDF reader.");
      setIsProcessing(false);
      setPdfFile(null);
    }
  }, []);

  useEffect(() => {
    if (pdfFile) {
      loadPdf(pdfFile);
    }
  }, [pdfFile]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [chatHistory]);
  const handleCitationClick = (e) => {
    if (e.target.classList.contains('citation-button')) {
      const pageNum = parseInt(e.target.dataset.page, 10);
      if (!isNaN(pageNum)) {
        renderPage(pageNum);
      }
    }
  };

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('click', handleCitationClick);

      return () => {
        chatContainer.removeEventListener('click', handleCitationClick);
      };
    }
  }, [chatHistory, renderPage]);

  const fetchWithBackoff = async (url, options, retries = 3, delay = 1000) => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) {
        if (response.status === 429 && retries > 0) {
          await new Promise(res => setTimeout(res, delay));
          return fetchWithBackoff(url, options, retries - 1, delay * 2);
        }
        throw new Error(`API Error: ${response.status} ${response.statusText}`);
      }
      return response.json();
    } catch (error) {
      if (retries > 0) {
        await new Promise(res => setTimeout(res, delay));
        return fetchWithBackoff(url, options, retries - 1, delay * 2);
      }
      throw error;
    }
  };

  const handleSendChat = async (query) => {
    if (!query || isLoadingAiResponse || !fullDocumentText) return;

    setIsLoadingAiResponse(true);
    const newUserMessage = { from: 'user', text: query };
    setChatHistory(prev => [...prev, newUserMessage]);

    const systemPrompt = `You are an AI assistant. You will be given the full text of a document, with page numbers clearly marked (e.g., [Page 1], [Page 2]). The user will ask a question about this document. 
Your task is to answer the user's question based *only* on the provided document text. 
When you answer, you MUST cite the page number(s) you used for your information. Format your citations *exactly* like this: [Page X]. 
If the information spans multiple pages, cite all relevant pages (e.g., [Page 2], [Page 4]).
Be concise and helpful. If the answer is not in the document, say so.`;

    const payload = {
      contents: [{
        role: "user",
        parts: [
          { text: "DOCUMENT TEXT:\n" + fullDocumentText },
          { text: "\n\nUSER QUESTION:\n" + query }
        ]
      }],
      systemInstruction: {
        parts: [{ text: systemPrompt }]
      },
    };

    try {
      const result = await fetchWithBackoff(GEMINI_API_URL + API_KEY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const text = result.candidates?.[0]?.content?.parts?.[0]?.text;

      if (text) {
        const parsedText = text.replace(
          /\[Page (\d+)\]/g,
          (match, pageNum) =>
            `<button class="citation-button" data-page="${pageNum}">${match}</button>`
        );

        setChatHistory(prev => [...prev, { from: 'ai', text: parsedText, isHtml: true }]);
      } else {
        throw new Error("Invalid response structure from API.");
      }
    } catch (e) {
      console.error("Error calling Gemini API:", e);
      setChatHistory(prev => [...prev, { from: 'ai', text: "Sorry, I encountered an error trying to answer your question." }]);
    } finally {
      setIsLoadingAiResponse(false);
    }
  };

  const renderUploadScreen = () => (
    <div className="flex items-center justify-center w-full h-full bg-gray-50">
      <div className="p-8 bg-white rounded-lg shadow-md border border-gray-200 text-center w-full max-w-md">
        <label
          htmlFor="file-upload"
          className={`cursor-pointer flex flex-col items-center justify-center p-10 border-2 border-dashed border-violet-200 rounded-lg bg-violet-50 transition-colors ${isScriptLoaded ? 'hover:bg-violet-100' : 'opacity-50 cursor-not-allowed'}`}
        >
          <UploadIcon className="w-12 h-12 text-violet-500 mb-4" />
          <span className="text-lg font-semibold text-gray-700">Upload PDF to start chatting</span>
          <span className="text-sm text-gray-500 mt-1">Click or drag and drop your file here</span>
          <input
            id="file-upload"
            type="file"
            className="hidden"
            accept="application/pdf"
            disabled={!isScriptLoaded}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setPdfFile(e.target.files[0]);
              }
            }}
          />
        </label>
        {!isScriptLoaded && <p className="text-sm text-gray-500 mt-2 animate-pulse">Loading PDF viewer...</p>}
        {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
      </div>
    </div>
  );

  const renderLoadingScreen = () => (
    <div className="flex items-center justify-center w-full h-full bg-white">
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 border-4 border-t-violet-500 border-gray-200 rounded-full animate-spin"></div>
        <span className="text-lg font-semibold text-gray-700 mt-4">Processing PDF...</span>
        <span className="text-sm text-gray-500">Extracting text and preparing your document.</span>
      </div>
    </div>
  );

  const renderMainInterface = () => (
    <div className="flex h-full w-full bg-white shadow-2xl overflow-hidden">
      <div className="w-full md:w-1/2 lg:w-1/3 flex flex-col border-r border-gray-200 h-full">
        <header className="flex items-center p-4 border-b border-gray-200 bg-gray-50">
          <button onClick={() => {
            setPdfFile(null);
            setPdfDoc(null);
            setFullDocumentText("");
            setChatHistory([]);
            setError(null);
            setIsProcessing(false);
            setTotalPages(0);
          }}>
            <CloseIcon className="w-6 h-6 text-gray-500 hover:text-gray-800" />
          </button>
          <h2 className="text-lg font-semibold text-gray-800 ml-4 truncate" title={pdfFile?.name}>
            {pdfFile?.name || "Document"}
          </h2>
        </header>

        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {chatHistory.map((msg, index) => (
            <div key={index} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`p-3 rounded-lg max-w-xs lg:max-w-md ${msg.from === 'user'
                  ? 'bg-violet-500 text-white'
                  : 'bg-gray-100 text-gray-800'
                  }`}
              >
                {msg.isHtml ? (
                  <div
                    className="prose prose-sm prose-violet"
                    dangerouslySetInnerHTML={{ __html: msg.text }}
                  />
                ) : (
                  <p className="text-sm">{msg.text}</p>
                )}
              </div>
            </div>
          ))}
          {isLoadingAiResponse && (
            <div className="flex justify-start">
              <div className="p-3 rounded-lg bg-gray-100 text-gray-800">
                <div className="flex items-center space-x-1">
                  <span className="text-sm">Typing</span>
                  <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-1 h-1 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          )}
        </div>

        <ChatInput onSend={handleSendChat} disabled={isLoadingAiResponse || isProcessing} />
      </div>
      <div className="hidden md:flex flex-col w-1/2 lg:w-2/3 h-full bg-gray-100">
        <div className="flex-1 overflow-auto flex justify-center py-4">
          <div className="shadow-lg">
            {Array.from(new Array(totalPages), (el, index) => (
              <PdfPageRenderer key={index + 1} pageNum={index + 1} pdfDoc={pdfDoc} />
            ))}
          </div>
        </div>
        {error && (
          <div className="absolute bottom-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-2 rounded-md shadow-lg">
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="h-screen w-screen bg-gray-100 font-sans">
      <style>{`
        .prose {
          color: inherit;
        }
        .prose-sm {
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .prose-sm p {
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }
        .prose-sm button {
          margin: 0 0.25em;
        }
        .citation-button {
          background-color: #e9d5ff; /* purple-200 */
          color: #5b21b6; /* violet-700 */
          font-weight: 600;
          padding: 0.125rem 0.5rem;
          border-radius: 0.375rem; /* rounded-md */
          border: 1px solid #c4b5fd; /* violet-300 */
          cursor: pointer;
          transition: all 0.2s;
        }
        .citation-button:hover {
          background-color: #d8b4fe; /* purple-300 */
          transform: translateY(-1px);
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
        }
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #f1f5f9; /* gray-100 */
        }
        ::-webkit-scrollbar-thumb {
          background: #cbd5e1; /* gray-300 */
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #94a3b8; /* gray-400 */
        }
      `}</style>

      {!pdfFile
        ? renderUploadScreen()
        : isProcessing
          ? renderLoadingScreen()
          : renderMainInterface()
      }
    </div>
  );
}

function ChatInput({ onSend, disabled }) {
  const [query, setQuery] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      onSend(query.trim());
      setQuery("");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 bg-white">
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled}
          placeholder="Ask about the document..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent disabled:bg-gray-100 text-sm"
        />
        <button
          type="submit"
          disabled={disabled || !query.trim()}
          className="p-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <SendIcon className="w-5 h-5" />
        </button>
      </div>
    </form>
  );
}

function UploadIcon(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  );
}

function CloseIcon(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function SendIcon(props) {
  return (
    <svg {...props} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
    </svg>
  );
}
